import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { CSSProperties, ReactNode } from 'react'

export interface ISortableListProps {
  /** Stable ids of the rows, in the current order. */
  ids: string[]
  /** Called with the old and new index of the dropped row. */
  onReorder: (from: number, to: number) => void
  children: ReactNode
}

/**
 * Vertical list reordered by dragging each row's handle (`useSortableRow`). The keyboard works
 * too: focus the handle, Space to lift, arrows to move, Space to drop.
 */
export default function SortableList({ ids, onReorder, children }: ISortableListProps) {
  const sensors = useSensors(
    // A small distance keeps a click on the handle from starting a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return

    const from = ids.indexOf(String(active.id))
    const to = ids.indexOf(String(over.id))
    if (from >= 0 && to >= 0) onReorder(from, to)
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {children}
      </SortableContext>
    </DndContext>
  )
}

/** Wiring for one row of a `SortableList`: the row takes `setNodeRef` and `style`, the handle takes `handleProps`. */
export function useSortableRow(id: string) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id
  })

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    position: 'relative',
    zIndex: isDragging ? 1 : undefined
  }

  return { setNodeRef, style, isDragging, handleProps: { ref: setActivatorNodeRef, ...attributes, ...listeners } }
}
